import Choices from "choices.js";
import "choices.js/public/assets/styles/choices.min.css";

const SELECTOR = 'select.form-select:not([data-no-choices])';

function initSelect(select: HTMLSelectElement): void {
    if (select.dataset.choicesInitialised === "1") return;
    select.dataset.choicesInitialised = "1";

    const instance = new Choices(select, {
        searchEnabled: true,
        itemSelectText: "",
        shouldSort: false,
        // A multi-select (e.g. Specialisms) shows its selections as tags; give each a remove button, and
        // don't cap search results at the default 4, which hides most matches in a long list.
        ...(select.multiple && {
            removeItemButton: true,
            searchResultLimit: select.options.length,
            callbackOnCreateTemplates: () => ({
                // Choices names each remove button "Remove item: '<value>'", and the value is often an ID
                // (a GUID for Specialisms) — no use to a screen reader. Name it after the visible label instead.
                item(this: Choices, ...args: Parameters<typeof Choices.defaults.templates.item>) {
                    const el = Choices.defaults.templates.item.apply(this, args);
                    const button = el.querySelector<HTMLButtonElement>('[data-button]');
                    if (button) {
                        const label = el.textContent?.replace(button.textContent ?? '', '').trim();
                        button.setAttribute('aria-label', `Remove ${label}`);
                    }
                    return el;
                },
            }),
        }),
    });

    // Expose on the element so other services can call setChoiceByValue() on it
    (select as any)._choices = instance;

    // Choices.js conceals the original select via the `hidden` property, which zeroes its
    // offsetWidth/Height and makes aspnet-client-validation skip it (isHidden() check).
    // Un-hide it so our CSS positional hiding (1×1, opacity 0) applies instead.
    select.hidden = false;
    select.style.display = '';
    select.tabIndex = -1;

    // aspnet-client-validation adds input-validation-error to the native select.
    // Mirror it to .choices__inner so the user sees the Bootstrap invalid border.
    const inner = select.closest<HTMLElement>('.choices')?.querySelector<HTMLElement>('.choices__inner');
    if (inner) {
        new MutationObserver(() => {
            inner.classList.toggle('is-invalid', select.classList.contains('input-validation-error'));
        }).observe(select, { attributes: true, attributeFilter: ['class'] });
    }
}

function initChoicesInSubtree(root: HTMLElement): void {
    if (root.matches(SELECTOR)) {
        initSelect(root as HTMLSelectElement);
    }
    root.querySelectorAll<HTMLSelectElement>(SELECTOR).forEach(initSelect);
}

export function initChoices(): void {
    initChoicesInSubtree(document.body);

    // Re-run on any DOM additions so dynamically inserted content (e.g. delegate
    // rows stamped from a <template>) is picked up without any extra wiring.
    new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node instanceof HTMLElement) {
                    initChoicesInSubtree(node);
                }
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
}
